import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as PlusIcon } from "../../asserts/logo/PlusIcon.svg";
import { parseCustomItemCards } from "./testStrategyCards";

const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const BTN_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const CANCEL_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.08)";
const CANCEL_BG =
  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), #F5F5F5";

const btnTextSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
};

const scrollAreaSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "scroll",
  overflowX: "hidden",
  px: { xs: 2, sm: "16px" },
  py: { xs: 2, sm: "24px" },
  bgcolor: "#FFFFFF",
  scrollbarGutter: "stable",
  scrollbarWidth: "auto",
  scrollbarColor: "#C1C1C1 #FAFAFA",
  "&::-webkit-scrollbar": {
    width: 14,
    height: 14,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#FAFAFA",
    border: "1px solid #E8E8E7",
    borderRadius: 0,
  },
  "&::-webkit-scrollbar-thumb": {
    boxSizing: "border-box",
    width: 8,
    minHeight: 24,
    backgroundColor: "#C1C1C1",
    borderRadius: "4px",
    borderTop: "2px solid #FAFAFA",
    borderBottom: "2px solid #FAFAFA",
    borderLeft: "3px solid #FAFAFA",
    borderRight: "3px solid #FAFAFA",
    backgroundClip: "padding-box",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#C1C1C1",
  },
  "&::-webkit-scrollbar-button": {
    display: "none",
    width: 0,
    height: 0,
  },
  "&::-webkit-scrollbar-corner": {
    backgroundColor: "#FAFAFA",
  },
};

const editableFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
    boxShadow: "none",
    "& fieldset": { borderColor: "#707070", borderWidth: 1 },
    "&:hover fieldset": { borderColor: "#707070" },
    "&.Mui-focused": { boxShadow: "none" },
    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: 1 },
  },
  "& .MuiInputBase-input": {
    py: "8px",
    px: "10px",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#98A2B3",
    opacity: 1,
  },
};

const readOnlyFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: "4px",
    bgcolor: "#F5F5F5",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
    "& fieldset": { borderColor: "#E6E6E6", borderWidth: 1 },
    "&:hover fieldset": { borderColor: "#E6E6E6" },
    "&.Mui-focused fieldset": { borderColor: "#E6E6E6", borderWidth: 1 },
  },
  "& .MuiInputBase-input": {
    py: "8px",
    px: "10px",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
    cursor: "default",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
};

const descriptionFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    display: "flex",
    alignItems: "stretch",
    boxSizing: "border-box",
    minHeight: 58,
    padding: "8px 10px",
    overflow: "hidden",
    resize: "vertical",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    color: "#000000",
    boxShadow: "none !important",
    filter: "none",
    "& fieldset": {
      borderColor: "#707070",
      borderWidth: "1px !important",
      boxShadow: "none",
      pointerEvents: "none",
    },
    "&:hover fieldset": { borderColor: "#707070", borderWidth: "1px !important" },
    "&.Mui-focused": { boxShadow: "none !important", filter: "none" },
    "&.Mui-focused fieldset": {
      borderColor: "#707070",
      borderWidth: "1px !important",
      boxShadow: "none",
    },
  },
  "& .MuiInputBase-input, & .MuiOutlinedInput-input, & textarea": {
    boxSizing: "border-box",
    flex: 1,
    alignSelf: "stretch",
    width: "100% !important",
    maxWidth: "100%",
    height: "100% !important",
    minHeight: 0,
    margin: 0,
    padding: "0 !important",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#000000",
    whiteSpace: "pre-wrap",
    overflowWrap: "normal",
    wordBreak: "normal",
    boxShadow: "none",
    textShadow: "none",
    filter: "none",
    outline: "none",
    appearance: "none",
    resize: "none",
    overflow: "auto",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#98A2B3",
    opacity: 1,
  },
};

const readOnlyDescriptionFieldSx = {
  ...descriptionFieldSx,
  "& .MuiOutlinedInput-root": {
    ...descriptionFieldSx["& .MuiOutlinedInput-root"],
    bgcolor: "#F5F5F5",
    resize: "none",
    "& fieldset": {
      borderColor: "#E6E6E6",
      borderWidth: "1px !important",
      boxShadow: "none",
      pointerEvents: "none",
    },
    "&:hover fieldset": { borderColor: "#E6E6E6", borderWidth: "1px !important" },
    "&.Mui-focused fieldset": {
      borderColor: "#E6E6E6",
      borderWidth: "1px !important",
      boxShadow: "none",
    },
  },
  "& .MuiInputBase-input, & .MuiOutlinedInput-input, & textarea": {
    ...descriptionFieldSx["& .MuiInputBase-input, & .MuiOutlinedInput-input, & textarea"],
    cursor: "default",
    resize: "none",
  },
};

const cancelBtnSx = {
  ...btnTextSx,
  boxSizing: "border-box",
  height: 40,
  minHeight: 40,
  minWidth: 0,
  width: "fit-content",
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  borderColor: "#BBBBBB",
  backgroundColor: "#F5F5F5",
  backgroundImage: CANCEL_BG,
  color: "#555555",
  boxShadow: CANCEL_SHADOW,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  justifyContent: "center",
  alignItems: "center",
  "&:hover": {
    border: "1px solid #BBBBBB",
    borderColor: "#BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: CANCEL_BG,
    color: "#555555",
    boxShadow: CANCEL_SHADOW,
  },
  "&:focus, &:focus-visible": {
    border: "1px solid #BBBBBB",
    borderColor: "#BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: CANCEL_BG,
    color: "#555555",
    boxShadow: CANCEL_SHADOW,
  },
  "&.Mui-disabled": {
    border: "1px solid #BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: CANCEL_BG,
    color: "#555555",
    opacity: 0.5,
  },
};

const primaryBtnSx = {
  ...btnTextSx,
  height: 40,
  minHeight: 40,
  minWidth: 0,
  width: "fit-content",
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid transparent",
  color: "#FFFFFF",
  backgroundImage: PRIMARY_BG,
  boxShadow: BTN_SHADOW,
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    backgroundImage: PRIMARY_BG_HOVER,
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: 1,
    cursor: "not-allowed",
  },
};

function RequiredMark() {
  return (
    <Typography component="span" sx={{ color: "#DE350B", mr: 0.25, fontWeight: 700 }}>
      *
    </Typography>
  );
}

function FieldLabel({ children, required }) {
  return (
    <Typography
      sx={{
        fontFamily: "Lato, sans-serif",
        fontSize: 14,
        fontWeight: 700,
        lineHeight: "21px",
        letterSpacing: 0,
        color: "#707070",
        mb: "8px",
      }}
    >
      {required ? <RequiredMark /> : null}
      {children}
    </Typography>
  );
}

function emptySubsection() {
  return {
    id: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    strategyName: "",
    name: "",
    description: "",
    isNew: true,
  };
}

function subsectionFromItem(it, i) {
  const cards = parseCustomItemCards(it);
  const card = cards[0] || { title: "", text: it?.body || "" };
  const heading = it?.heading || "";
  const cardTitle = card.title || "";
  const usedNamedBody = Boolean(cardTitle);
  return {
    id: `sub-${i}-${Date.now()}`,
    strategyName: usedNamedBody ? heading : "",
    name: usedNamedBody ? cardTitle : heading,
    description: card.text || it?.body || "",
    isNew: false,
  };
}

function subsectionComplete(s) {
  return Boolean(s.strategyName?.trim() && s.name?.trim() && s.description?.trim());
}

export default function AddCustomStrategyDrawer({
  open,
  onClose,
  onSave,
  initialSection = null,
  editItemIndex = null,
}) {
  const isAdd = !initialSection;
  const editDescriptionOnly = initialSection != null && editItemIndex != null;
  const isFullEdit = initialSection != null && editItemIndex == null;
  const [mainsectionName, setMainsectionName] = useState("");
  const [subsections, setSubsections] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialSection) {
      setMainsectionName(initialSection.title || "");
      setSubsections((initialSection.items || []).map(subsectionFromItem));
      return;
    }
    setMainsectionName("");
    setSubsections([]);
  }, [open, initialSection]);

  const canSave = useMemo(() => {
    if (editDescriptionOnly) {
      return subsections.every((s, idx) => {
        if (s.isNew) return subsectionComplete(s);
        if (idx === editItemIndex) return Boolean(s.description.trim());
        return subsectionComplete(s);
      });
    }
    if (!mainsectionName.trim() || subsections.length === 0) return false;
    return subsections.every(subsectionComplete);
  }, [mainsectionName, subsections, editDescriptionOnly, editItemIndex]);

  const addSubsection = () => {
    setSubsections((prev) => [...prev, emptySubsection()]);
  };

  const updateSubsection = (id, field, value) => {
    setSubsections((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleSave = async () => {
    if (!canSave) return;
    const items = subsections
      .filter(subsectionComplete)
      .map((s) => {
        const name = s.name.trim();
        const description = s.description.trim();
        const strategy = s.strategyName.trim();
        return {
          heading: strategy || name,
          body: strategy ? `${name}: ${description}` : description,
        };
      });
    try {
      setSubmitting(true);
      await onSave({
        id: initialSection?.id,
        title: mainsectionName.trim(),
        items,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const drawerTitle = isAdd
    ? "Add Custom Strategy"
    : "Edit Custom Strategy";
  const drawerSubtitle = editDescriptionOnly
    ? "Modify strategy details, subsections."
    : "Define a test strategy not covered in the auto-generated plan.";
  const footerNote = editDescriptionOnly
    ? "Changes saved automatically."
    : "Saved to Custom Test Strategies.";
  const saveLabel = editDescriptionOnly || isFullEdit ? "Update Strategy" : "Save Strategy";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100vw",
          height: "100%",
          bgcolor: "#FFFFFF",
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#FFFFFF", minWidth: 0 }}>
        <Box
          sx={{
            flexShrink: 0,
            boxSizing: "border-box",
            minHeight: 88,
            pl: { xs: 2, sm: "24px" },
            pr: { xs: 2, sm: "16px" },
            py: "16px",
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #E6E6E6",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px",
                letterSpacing: 0,
                color: "#555555",
              }}
            >
              {drawerTitle}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "24px",
                letterSpacing: 0,
                color: "#555555",
              }}
            >
              {drawerSubtitle}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              width: 24,
              height: 24,
              p: 0,
              color: "#555555",
              flexShrink: 0,
              "& .MuiSvgIcon-root": { fontSize: 16 },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={scrollAreaSx}>
          <Stack spacing="16px" sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel required={!editDescriptionOnly}>Mainsection Name</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Custom Boundary Value Analysis"
                value={mainsectionName}
                onChange={(e) => setMainsectionName(e.target.value)}
                InputProps={{ readOnly: editDescriptionOnly }}
                sx={editDescriptionOnly ? readOnlyFieldSx : editableFieldSx}
              />
            </Box>

            {subsections.map((sub, idx) => {
              const nameEditable = !editDescriptionOnly || sub.isNew;
              const descriptionEditable = !editDescriptionOnly || idx === editItemIndex || sub.isNew;
              return (
              <Box
                key={sub.id}
                sx={{
                  pt: idx === 0 ? 0 : 0,
                  borderTop: idx > 0 ? "1px solid #E6E6E6" : "none",
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: "21px",
                    letterSpacing: 0,
                    color: "#555555",
                    mb: "8px",
                    mt: idx > 0 ? "16px" : 0,
                  }}
                >
                  Sub Section {String(idx + 1).padStart(2, "0")}
                </Typography>
                <Stack spacing="16px">
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel required={nameEditable}>Strategy Name</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Boundary Value Analysis for Payment Thresholds"
                      value={sub.strategyName}
                      onChange={(e) => updateSubsection(sub.id, "strategyName", e.target.value)}
                      InputProps={{ readOnly: !nameEditable }}
                      sx={nameEditable ? editableFieldSx : readOnlyFieldSx}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel required={nameEditable}>Subsection Name</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g., Boundary Value Analysis for Payment Thresholds"
                      value={sub.name}
                      onChange={(e) => updateSubsection(sub.id, "name", e.target.value)}
                      InputProps={{ readOnly: !nameEditable }}
                      sx={nameEditable ? editableFieldSx : readOnlyFieldSx}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel required={descriptionEditable}>Description</FieldLabel>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      placeholder="Describe the testing methodology, expected outcomes, and acceptance criteria..."
                      value={sub.description}
                      onChange={(e) => updateSubsection(sub.id, "description", e.target.value)}
                      InputProps={{ readOnly: !descriptionEditable }}
                      sx={descriptionEditable ? descriptionFieldSx : readOnlyDescriptionFieldSx}
                    />
                  </Box>
                </Stack>
              </Box>
            );
            })}

            <Button
              disableElevation
              disableRipple
              variant="outlined"
              disabled={!mainsectionName.trim()}
              startIcon={
                <Box
                  component={PlusIcon}
                  sx={{ width: 16, height: 16, display: "block", flexShrink: 0 }}
                />
              }
              onClick={addSubsection}
              sx={{
                boxSizing: "border-box",
                alignSelf: "flex-start",
                minWidth: 0,
                width: "fit-content",
                height: 32,
                minHeight: 32,
                px: 0,
                pt: "4px",
                pr: "24px",
                pb: "4px",
                pl: "12px",
                gap: "4px",
                borderRadius: "4px",
                border: "1px solid #005BF0",
                borderColor: "#005BF0",
                bgcolor: "#FFFFFF",
                color: "#005BF0",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                lineHeight: "24px",
                letterSpacing: 0,
                textTransform: "none",
                verticalAlign: "middle",
                justifyContent: "center",
                alignItems: "center",
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": {
                  m: 0,
                  display: "flex",
                  alignItems: "center",
                },
                "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
                  border: "1px solid #005BF0",
                  borderColor: "#005BF0",
                  bgcolor: "#F0F6FF",
                  backgroundColor: "#F0F6FF",
                  color: "#005BF0",
                  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
                },
                "&:focus:not(.Mui-disabled):not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
                  outline: "none",
                  border: "2px solid #005BF0",
                  borderColor: "#005BF0",
                  bgcolor: "#FFFFFF !important",
                  backgroundColor: "#FFFFFF !important",
                  backgroundImage: "none !important",
                  color: "#005BF0",
                  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
                },
                "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
                  bgcolor: "#FFFFFF !important",
                  backgroundColor: "#FFFFFF !important",
                  backgroundImage: "none !important",
                  border: "2px solid #005BF0",
                  borderColor: "#005BF0",
                  color: "#005BF0",
                },
                "&:active:not(.Mui-disabled)": {
                  border: "1px solid #005BF0",
                  borderColor: "#005BF0",
                  bgcolor: "#FFFFFF !important",
                  backgroundColor: "#FFFFFF !important",
                  backgroundImage: "none !important",
                  color: "#005BF0",
                  boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)",
                },
                "& .MuiTouchRipple-root": { display: "none" },
                "&.Mui-disabled": {
                  bgcolor: "#F5F5F5",
                  border: "1px solid #E6E6E6",
                  borderColor: "#E6E6E6",
                  color: "#BBBBBB",
                  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
                  cursor: "not-allowed",
                  "& svg path": { fill: "#BBBBBB" },
                },
              }}
            >
              Add Subsection
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            boxSizing: "border-box",
            width: "100%",
            minHeight: 88,
            px: { xs: 2, sm: "24px" },
            py: { xs: 2, sm: "24px" },
            bgcolor: "#F5F5F5",
            borderTop: "1px solid #E6E6E6",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.5, sm: "16px" },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontSize: 13,
              lineHeight: "24px",
              letterSpacing: 0,
              color: "#555555",
              flex: { sm: "1 1 310px" },
              minWidth: 0,
            }}
          >
            {footerNote}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "12px",
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              disableElevation
              disableRipple
              variant="outlined"
              onClick={onClose}
              disabled={submitting}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              disableRipple
              variant="contained"
              onClick={handleSave}
              disabled={!canSave || submitting}
              sx={primaryBtnSx}
            >
              {saveLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
